import React, { useState, useMemo, useCallback } from 'react';
import { X, Search, CheckSquare, Square } from 'lucide-react';
import type { PortfolioSite } from '../../redux/api/portfolioApi';

interface CustomPortfolioEditorProps {
  isOpen: boolean;
  onClose: () => void;
  sites: PortfolioSite[];
  selectedIds: string[];
  onSave: (ids: string[]) => void;
}

const CustomPortfolioEditor: React.FC<CustomPortfolioEditorProps> = ({
  isOpen,
  onClose,
  sites,
  selectedIds,
  onSave,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedIds));
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSites = useMemo(() => {
    if (!searchTerm) return sites;
    const lower = searchTerm.toLowerCase();
    return sites.filter(s =>
      (s.site_name || s.name || '').toLowerCase().includes(lower) ||
      (s.location || '').toLowerCase().includes(lower)
    );
  }, [sites, searchTerm]);

  const toggleSite = useCallback((siteId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(filteredSites.map(s => s.site_id || s.id || '')));
  }, [filteredSites]);

  const deselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleSave = useCallback(() => {
    onSave(Array.from(selected));
    onClose();
  }, [selected, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <div className="eg-modal-backdrop" onClick={onClose}>
      <div className="eg-modal-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Edit Portfolio
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
          Select sites to include in your custom portfolio view.
        </p>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            placeholder="Search sites..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="eg-input-search"
          />
        </div>

        {/* Bulk actions */}
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={selectAll}
            className="text-xs font-medium text-[#17c480] hover:text-[#17c480]/80 transition-colors"
          >
            Select all
          </button>
          <button
            onClick={deselectAll}
            className="text-xs font-medium transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Deselect all
          </button>
          <span className="text-xs ml-auto" style={{ color: 'var(--color-text-muted)' }}>
            {selected.size} of {sites.length} selected
          </span>
        </div>

        {/* Site list */}
        <div className="max-h-64 overflow-y-auto space-y-1 rounded-lg" style={{ border: '1px solid var(--color-border)' }}>
          {filteredSites.map(site => {
            const id = site.site_id || site.id || '';
            const isSelected = selected.has(id);
            return (
              <button
                key={id}
                onClick={() => toggleSite(id)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-[var(--color-surface-hover)]"
              >
                {isSelected ? (
                  <CheckSquare className="w-4 h-4 text-[#17c480] flex-shrink-0" />
                ) : (
                  <Square className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                    {site.site_name || site.name}
                  </p>
                  {site.location && (
                    <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                      {site.location}
                    </p>
                  )}
                </div>
                {(site.site_capacity_mw || site.capacity_mw) && (
                  <span className="text-xs flex-shrink-0" style={{ color: 'var(--color-text-muted)' }}>
                    {site.site_capacity_mw || site.capacity_mw} MW
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="eg-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="eg-btn-primary">
            Save Portfolio
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomPortfolioEditor;
